import {
  generateId,
  generateNumbersInRangeWithDuplicates,
  getNovelLine,
  getRandomText,
} from '@freelancer/datastore/testing';
import { EntryStatusApi } from 'api-typings/contests/contests';
import { ContestEntry } from './contest-entries.model';

export interface GenerateContestEntriesOptions {
  readonly freelancerIds: ReadonlyArray<number>;
  readonly contestId: number;
  readonly contestOwnerId: number;
  readonly contestPrize?: number;

  // These are user IDs
  // They map one-to-one to entry.status, so only one will be applied.
  readonly activeIds?: ReadonlyArray<number>;
  readonly pendingIds?: ReadonlyArray<number>;
  readonly withdrawnIds?: ReadonlyArray<number>;
  readonly eliminatedIds?: ReadonlyArray<number>;
  readonly chosenIds?: ReadonlyArray<number>;
  readonly wonIds?: ReadonlyArray<number>;
  readonly boughtIds?: ReadonlyArray<number>;

  readonly minSellPrice?: number;
  readonly maxSellPrice?: number;
  readonly minLikeCount?: number;
  readonly maxLikeCount?: number;
  readonly minRating?: number;
  readonly maxRating?: number;
}

// TODO: files
export function generateContestEntryObjects({
  freelancerIds,
  contestId,
  contestOwnerId,
  contestPrize = 100,
  activeIds = freelancerIds, // by default, entries are active
  pendingIds = [],
  withdrawnIds = [],
  eliminatedIds = [],
  chosenIds = [],
  wonIds = [],
  boughtIds = [],
  minSellPrice = 1,
  maxSellPrice = contestPrize,
  minLikeCount = 0,
  maxLikeCount = 20,
  minRating, // by default, entries are unrated
  maxRating,
}: GenerateContestEntriesOptions): ReadonlyArray<ContestEntry> {
  const sellPrices = generateNumbersInRangeWithDuplicates(
    minSellPrice,
    maxSellPrice,
    freelancerIds.length,
    'sellPrices',
  );

  const likeCounts = generateNumbersInRangeWithDuplicates(
    minLikeCount,
    maxLikeCount,
    freelancerIds.length,
    'likeCounts',
  );

  let ratings: ReadonlyArray<number> = [];
  if (minRating || maxRating) {
    ratings = generateNumbersInRangeWithDuplicates(
      minRating ?? 1,
      maxRating ?? 5,
      freelancerIds.length,
      'entryRatings',
    );
  }

  const now = Date.now();

  return freelancerIds.map((freelancerId, index) => ({
    id: generateId(),
    ownerId: freelancerId,
    contestId,
    contestOwnerId,
    number: index + 1,
    title: getRandomText(0, 50),
    description: getNovelLine('prideAndPrejudice', index),
    rating: ratings.length ? ratings[index] : undefined,
    sellPrice: sellPrices[index],
    likeCount: likeCounts[index],

    timeEntered: now,
    timeEliminated: undefined, // TODO
    timeWon: undefined, // TODO

    ...(activeIds.includes(freelancerId)
      ? { status: EntryStatusApi.ACTIVE }
      : pendingIds.includes(freelancerId)
      ? { status: EntryStatusApi.PENDING }
      : withdrawnIds.includes(freelancerId)
      ? { status: EntryStatusApi.WITHDRAWN }
      : eliminatedIds.includes(freelancerId)
      ? { status: EntryStatusApi.ELIMINATED }
      : chosenIds.includes(freelancerId)
      ? { status: EntryStatusApi.CHOSEN }
      : wonIds.includes(freelancerId)
      ? { status: EntryStatusApi.WON }
      : boughtIds.includes(freelancerId)
      ? { status: EntryStatusApi.BOUGHT }
      : { status: EntryStatusApi.ACTIVE }),
  }));
}

// --- Mixins ---
